import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../lib/store";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  TrendingUp,
  PlusCircle,
  CheckSquare,
  Film,
  Settings,
  MessageSquare,
  LogOut,
  Zap,
  BarChart3,
  FileText,
  Calendar,
  Music,
  Image,
  Link as LinkIcon,
  Menu,
  X,
  Sparkles,
  CreditCard,
  Crown,
  Video,
  Clock,
  AlertCircle,
  Gift,
  Stethoscope,
  Palette,
  Baby,
  Users,
  UserCog,
  HardDrive,
  Coins,
  Headphones,
} from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import NotificationBell from "../NotificationBell";
import CreditBalance from "../CreditBalance";
import BuyCreditsModal from "../BuyCreditsModal";
import { BASE_URL } from "../../lib/api";

const API_URL = BASE_URL;

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/trends", icon: TrendingUp, label: "Trends" },
  { path: "/create", icon: PlusCircle, label: "Create Video" },
  { path: "/ugc-studio", icon: Sparkles, label: "UGC Ad Studio", isNew: true, requiresUgc: true },
  { path: "/viral-maker", icon: Baby, label: "Viral Maker", isNew: true, isTrending: true, requiresUgc: true },
  { path: "/script-doctor", icon: Stethoscope, label: "Script Doctor", isNew: true },
  { path: "/media-library", icon: HardDrive, label: "Media Library" },
  { path: "/viral-predictor", icon: BarChart3, label: "Viral Predictor" },
  { path: "/templates", icon: FileText, label: "Templates" },
  { path: "/image-studio", icon: Image, label: "Image Studio", isNew: true },
  { path: "/video-studio", icon: Film, label: "Video Studio", isNew: true },
  { path: "/calendar", icon: Calendar, label: "Calendar" },
  { path: "/sounds", icon: Music, label: "Sounds" },
  { path: "/tiktok", icon: LinkIcon, label: "TikTok Account" },
  { path: "/brand-kit", icon: Palette, label: "Brand Kit", isNew: true },
  { path: "/review", icon: CheckSquare, label: "Review Queue" },
  { path: "/engagement", icon: MessageSquare, label: "Engagement" },
  { path: "/admin", icon: UserCog, label: "Admin Dashboard", requiresAdmin: true, showStaffSupportBadge: true },
  { path: "/my-clients", icon: Users, label: "My Clients", requiresRole: ["account_manager", "staff", "admin"], showStaffSupportBadge: true },
  { path: "/referrals", icon: Gift, label: "Referrals", isNew: true },
  { path: "/support", icon: Headphones, label: "Help & Support", showUnreadBadge: true },
  { path: "/pricing", icon: CreditCard, label: "Pricing" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [staffPendingCount, setStaffPendingCount] = useState(0);

  // Check if user is staff (can handle support tickets)
  const isStaff = user?.is_admin || ['admin', 'account_manager', 'staff'].includes(user?.role);

  useEffect(() => {
    fetchSubscription();
    fetchSupportUnread();
    if (isStaff) {
      fetchStaffPendingCount();
    }
    // Poll for support notifications every 60 seconds
    const interval = setInterval(() => {
      fetchSupportUnread();
      if (isStaff) {
        fetchStaffPendingCount();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [isStaff]);

  const fetchSubscription = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchSupportUnread = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/support/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSupportUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching support unread count:', error);
    }
  };

  const fetchStaffPendingCount = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/support/pending-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStaffPendingCount(data.needs_attention || 0);
      }
    } catch (error) {
      console.error('Error fetching staff pending count:', error);
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend logout to clear session cookie
      const { authAPI } = await import("../../lib/api");
      await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear local state
    logout();
    navigate("/login");
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 md:p-6 border-b border-border/50">
        <NavLink to="/dashboard" className="flex items-center gap-3" onClick={handleNavClick}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-white tracking-tight">iGoViral</h1>
            <p className="text-xs text-muted-foreground">Content Creator</p>
          </div>
        </NavLink>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-1">
          {navItems.map((item) => {
            // Hide admin-only items
            if (item.requiresAdmin && !user?.is_admin) {
              return null;
            }
            
            // Hide items that require specific roles
            if (item.requiresRole && !item.requiresRole.includes(user?.role) && !user?.is_admin) {
              return null;
            }
            
            // Hide UGC Ad Studio if user doesn't have access
            if (item.requiresUgc && subscription && !subscription.ugc_access && !subscription.ugc_lite_access) {
              return (
                <NavLink
                  key={item.path}
                  to="/pricing"
                  onClick={handleNavClick}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 text-muted-foreground/50 hover:text-white hover:bg-secondary/50"
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                  <span className="ml-auto px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-400">
                    PRO
                  </span>
                </NavLink>
              );
            }
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30"
                      : "text-muted-foreground hover:text-white hover:bg-secondary/50"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {/* Staff support badge - shows pending tickets for account managers/staff */}
                {item.showStaffSupportBadge && isStaff && staffPendingCount > 0 && (
                  <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-orange-500 text-white animate-pulse">
                    {staffPendingCount}
                  </span>
                )}
                {/* User support unread badge */}
                {item.showUnreadBadge && supportUnreadCount > 0 && (
                  <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white animate-pulse">
                    {supportUnreadCount}
                  </span>
                )}
                {item.isTrending && subscription?.ugc_access && (
                  <span className="ml-auto px-2 py-0.5 text-[10px] font-bold rounded-full bg-gradient-to-r from-pink-500 to-orange-500 text-white animate-pulse">
                    HOT
                  </span>
                )}
                {item.isNew && !item.isTrending && !item.showUnreadBadge && subscription?.ugc_access && (
                  <span className="ml-auto px-2 py-0.5 text-[10px] font-bold rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white">
                    NEW
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Subscription Status */}
      {subscription && (
        <div className="px-3 py-2 border-t border-border/50">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {subscription.plan === 'pro' || subscription.plan === 'enterprise' ? (
                  <Crown className="h-4 w-4 text-amber-400" />
                ) : (
                  <Video className="h-4 w-4 text-blue-400" />
                )}
                <span className="text-xs font-medium text-white">{subscription.plan_name}</span>
              </div>
              <Badge 
                variant="outline" 
                className={`text-[10px] ${
                  subscription.plan === 'pro' ? 'border-amber-500/30 text-amber-400' :
                  subscription.plan === 'premium' ? 'border-purple-500/30 text-purple-400' :
                  'border-slate-500/30 text-slate-400'
                }`}
              >
                {subscription.max_duration}s
              </Badge>
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between text-[10px]">
                <span className="text-slate-400">Credits</span>
                <span className={subscription.videos_remaining <= 2 ? 'text-amber-400' : 'text-slate-300'}>
                  {subscription.videos_remaining}/{subscription.video_limit}
                </span>
              </div>
              <Progress 
                value={(subscription.videos_used / subscription.video_limit) * 100} 
                className="h-1"
              />
            </div>
            
            {subscription.videos_remaining === 0 && (
              <div className="flex items-center gap-1 mt-2 text-[10px] text-amber-400">
                <AlertCircle className="h-3 w-3" />
                <span>Quota exhausted</span>
              </div>
            )}
            
            {subscription.plan !== 'enterprise' && (
              <Button
                size="sm"
                variant="ghost"
                className="w-full mt-2 h-7 text-[10px] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                onClick={() => navigate('/pricing')}
              >
                Upgrade Plan
              </Button>
            )}
          </div>
        </div>
      )}

      {/* User Section */}
      <div className="p-4 border-t border-border/50">
        <div className="flex items-center gap-3 mb-4">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF0055] to-[#00F0FF] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-white hover:bg-destructive/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#0A0A0B] border-b border-border/50 px-4 py-3 flex items-center justify-between">
        <NavLink to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-white">iGoViral</span>
        </NavLink>
        <div className="flex items-center gap-2">
          <CreditBalance compact onBuyCredits={() => setShowBuyCreditsModal(true)} />
          <NotificationBell />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="md:hidden fixed top-0 left-0 bottom-0 w-72 bg-[#0A0A0B] border-r border-border/50 flex flex-col z-50"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={{ x: -280 }}
        animate={{ x: 0 }}
        className="hidden md:flex w-64 bg-[#0A0A0B] border-r border-border/50 flex-col"
      >
        <SidebarContent />
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-[#0A0A0B] flex flex-col">
        {/* Desktop Header Bar */}
        <div className="hidden md:flex items-center justify-end gap-4 px-6 py-3 border-b border-border/30">
          <CreditBalance onBuyCredits={() => setShowBuyCreditsModal(true)} />
          <NotificationBell />
          <div className="flex items-center gap-2">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name}
                className="w-8 h-8 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF0055] to-[#00F0FF] flex items-center justify-center text-white text-sm font-bold">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </div>
            )}
            <span className="text-sm text-white font-medium">{user?.name}</span>
          </div>
        </div>
        
        <div className="pt-16 md:pt-0 p-4 md:p-6 lg:p-10 flex-1 pb-8">
          <Outlet />
        </div>
        
        {/* Footer */}
        <footer className="border-t border-border/30 bg-[#0A0A0B]/95 py-4 px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <p>© {new Date().getFullYear()} iGoViral Content Creator. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="/terms" className="hover:text-[#00F0FF] transition-colors">Terms of Service</a>
              <a href="/privacy" className="hover:text-[#00F0FF] transition-colors">Privacy Policy</a>
            </div>
          </div>
        </footer>
      </main>

      {/* Buy Credits Modal */}
      <BuyCreditsModal 
        isOpen={showBuyCreditsModal} 
        onClose={() => setShowBuyCreditsModal(false)}
        onSuccess={() => {
          setShowBuyCreditsModal(false);
          // Optionally refresh credit balance
        }}
      />
    </div>
  );
}
